import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { ReactNode, useEffect, useMemo, useState } from 'react'

import { Button, Typography } from '~/components'
import { BookmarkEditorModal } from '~/components/chat/BookmarkEditorModal'
import { LockIcon, PeopleIcon, PlusIcon, SearchIcon } from '~/components/chat/bookmarkIcons'
import { useModal } from '~/components/modals/CurieModal'
import {
  BookmarkVisibility,
  PromptBookmark,
  useDeletePromptBookmark,
  usePromptBookmarks
} from '~/services/promptBookmarkService'
import { BetterToast } from '~/utils/toast'

dayjs.extend(relativeTime)

// ──────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────

function formatTs(ts?: string | null): string {
  if (!ts) return '—'
  const d = dayjs(ts)
  if (!d.isValid()) return '—'
  return d.fromNow()
}

function authorLabel(b: PromptBookmark): string {
  if (b.is_owner) return 'You'
  return b.author_name || b.author_email || 'Unknown'
}

function authorInitial(b: PromptBookmark): string {
  const source = b.author_name || b.author_email || ''
  return source ? source.charAt(0).toUpperCase() : '?'
}

// ──────────────────────────────────────────────────────────────────────
// Top-level form
// ──────────────────────────────────────────────────────────────────────

export default function BookmarksSettingsTab() {
  const { data, isLoading, error } = usePromptBookmarks()
  const deleteMut = useDeletePromptBookmark()
  const editorModal = useModal()
  const [editing, setEditing] = useState<PromptBookmark | null>(null)
  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState<BookmarkVisibility>('personal')

  const bookmarks = data?.bookmarks ?? []

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return bookmarks
    return bookmarks.filter(
      b => b.title.toLowerCase().includes(q) || b.body.toLowerCase().includes(q)
    )
  }, [bookmarks, search])

  const personal = useMemo(() => filtered.filter(b => b.visibility === 'personal'), [filtered])
  const shared = useMemo(() => filtered.filter(b => b.visibility === 'account'), [filtered])
  const activeItems = activeTab === 'personal' ? personal : shared

  useEffect(() => {
    if (activeTab === 'personal' && personal.length === 0 && shared.length > 0) {
      setActiveTab('account')
    } else if (activeTab === 'account' && shared.length === 0 && personal.length > 0) {
      setActiveTab('personal')
    }
  }, [activeTab, personal.length, shared.length])

  const openCreate = () => {
    setEditing(null)
    editorModal.open()
  }

  const openEdit = (b: PromptBookmark) => {
    setEditing(b)
    editorModal.open()
  }

  const onClose = () => {
    editorModal.close()
    // Defer clearing so the modal closes smoothly before the data unmounts.
    // Cache invalidation is handled by the create/update/delete mutation
    // hooks themselves (see invalidateBookmarks in promptBookmarkService),
    // so we deliberately do NOT call refetch() here — doing so would
    // double-fetch and race the in-flight invalidation.
    setTimeout(() => setEditing(null), 200)
  }

  const onDelete = async (b: PromptBookmark) => {
    if (!window.confirm(`Delete bookmark "${b.title || 'Untitled'}"?`)) return
    try {
      await deleteMut.mutateAsync({ uuid: b.uuid })
      BetterToast.success(`Deleted "${b.title || 'Untitled'}"`)
    } catch (ex) {
      BetterToast.error(ex instanceof Error ? ex.message : 'Failed to delete bookmark')
    }
    // No manual refetch — useDeletePromptBookmark.onSuccess already
    // invalidates the list query.
  }

  return (
    // px-4 keeps the heading and card stack aligned with SettingsPage's tab row.
    <div className='w-full flex flex-col gap-6 px-4' data-testid='bookmarks-form'>
      {/* Heading typography matches the other settings tabs (titleLg/bold,
          see ScheduledTasksSettingsTab) so Bookmarks doesn't read as a smaller,
          secondary surface. */}
      <header className='flex flex-col gap-1 select-none'>
        <Typography fontFamily='geist' fontSize='titleLg' fontWeight='bold'>
          Bookmarks
        </Typography>
        <Typography
          fontFamily='geist'
          fontSize='bodyMd'
          className='text-tokens-neutral-text-secondary max-w-[60ch]'
        >
          Saved prompts you can re-use from chat.
        </Typography>
      </header>

      <BookmarksHintCard />

      {/* Search + CTA. The input itself uses an inset shadow so it reads
          as "carved into" the page rather than floating; the New button
          uses the primary variant for clear visual hierarchy. */}
      <div className='flex items-center justify-between gap-3'>
        <div className='relative flex-1 max-w-[420px]'>
          <span
            className='absolute left-3 top-1/2 -translate-y-1/2 text-tokens-neutral-text-tertiary pointer-events-none'
            aria-hidden='true'
          >
            <SearchIcon />
          </span>
          <input
            type='text'
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder='Search bookmarks…'
            data-testid='bookmarks-form-search'
            className='w-full text-sm pl-9 pr-3 py-2 rounded-full bg-tokens-neutral-bg-grey-1 text-tokens-neutral-text-primary placeholder:text-tokens-neutral-text-tertiary border border-tokens-neutral-border-2 shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)] focus:outline-none focus:border-tokens-status-link-2 focus:ring-2 focus:ring-tokens-status-link-base transition-shadow'
          />
        </div>
        <Button onClick={openCreate} leftIcon={<PlusIcon />} data-testid='bookmarks-form-new'>
          New bookmark
        </Button>
      </div>

      {error ? (
        <div className='rounded-[12px] border border-tokens-status-error-1 bg-tokens-status-error-text/5 px-4 py-3'>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            className='text-tokens-status-error-text'
          >
            {`Failed to load bookmarks: ${error.message}`}
          </Typography>
        </div>
      ) : null}

      {isLoading ? (
        <Typography fontFamily='geist' fontSize='bodyMd'>
          Loading…
        </Typography>
      ) : filtered.length === 0 ? (
        <EmptyState totalCount={bookmarks.length} onCreate={openCreate} />
      ) : (
        <div className='flex flex-col gap-3'>
          <BookmarkVisibilityTabs
            activeTab={activeTab}
            onChange={setActiveTab}
            personalCount={personal.length}
            sharedCount={shared.length}
          />
          <div className='flex flex-col gap-2'>
            {activeItems.map(b => (
              <BookmarkCard
                key={b.uuid}
                bookmark={b}
                onEdit={() => openEdit(b)}
                onDelete={() => onDelete(b)}
              />
            ))}
          </div>
        </div>
      )}

      <BookmarkEditorModal
        isOpen={editorModal.isOpen}
        onClose={onClose}
        editing={editing}
        defaultBody=''
      />
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Empty state
// ──────────────────────────────────────────────────────────────────────

function EmptyState({ totalCount, onCreate }: { totalCount: number; onCreate: () => void }) {
  const isFirstTime = totalCount === 0
  return (
    <div
      className='w-full rounded-[16px] border border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-1 px-6 py-10 flex flex-col items-center text-center gap-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]'
      data-testid='bookmarks-form-empty'
    >
      <div className='w-12 h-12 rounded-full bg-tokens-neutral-bg-white border border-tokens-neutral-border-2 flex items-center justify-center text-tokens-neutral-text-tertiary shadow-segment'>
        <SearchIcon />
      </div>
      <Typography fontFamily='geist' fontSize='bodyMdMedium' fontWeight='semibold'>
        {isFirstTime ? 'No bookmarks yet' : 'No bookmarks match that search'}
      </Typography>
      <Typography
        fontFamily='geist'
        fontSize='bodyMd'
        className='text-tokens-neutral-text-secondary max-w-[420px]'
      >
        {isFirstTime
          ? 'Save a prompt from chat or create one here to get started.'
          : 'Try a different search term, or clear the box to see everything.'}
      </Typography>
      {isFirstTime && (
        <Button
          onClick={onCreate}
          className='mt-2'
          leftIcon={<PlusIcon />}
          data-testid='bookmarks-form-empty-cta'
        >
          Create your first bookmark
        </Button>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Section + row
// ──────────────────────────────────────────────────────────────────────

function BookmarkVisibilityTabs({
  activeTab,
  onChange,
  personalCount,
  sharedCount
}: {
  activeTab: BookmarkVisibility
  onChange: (tab: BookmarkVisibility) => void
  personalCount: number
  sharedCount: number
}) {
  const tabs: Array<{
    key: BookmarkVisibility
    label: string
    count: number
    icon: ReactNode
  }> = [
    { key: 'personal', label: 'Personal', count: personalCount, icon: <LockIcon /> },
    {
      key: 'account',
      label: 'Shared',
      count: sharedCount,
      icon: <PeopleIcon />
    }
  ]

  return (
    <div
      className='flex w-fit items-center gap-4 border-b border-tokens-neutral-border-2'
      role='tablist'
      aria-label='Bookmark visibility'
      data-testid='bookmarks-form-visibility-tabs'
    >
      {tabs.map(tab => {
        const selected = activeTab === tab.key
        return (
          <button
            key={tab.key}
            type='button'
            role='tab'
            aria-selected={selected}
            onClick={() => onChange(tab.key)}
            data-testid={`bookmarks-form-tab-${tab.key}`}
            className={`inline-flex min-w-0 items-center gap-2.5 border-b pb-2 text-base font-medium transition-colors -mb-px ${
              selected
                ? 'border-tokens-neutral-text-primary text-tokens-neutral-text-primary'
                : 'text-tokens-neutral-text-tertiary hover:text-tokens-neutral-text-primary'
            }`}
          >
            <span className='flex h-5 w-5 items-center justify-center shrink-0'>{tab.icon}</span>
            <span className='truncate'>{tab.label}</span>
            <span className='text-xs font-medium tabular-nums text-tokens-neutral-text-quaternary'>
              ({tab.count})
            </span>
          </button>
        )
      })}
    </div>
  )
}

function BookmarkCard({
  bookmark,
  onEdit,
  onDelete
}: {
  bookmark: PromptBookmark
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <div
      // Card: subtle outer + inset 1px highlight on hover (the "button"
      // shadow token is `inset 0 0 0 1px rgba(15,15,15,0.1) + 0 1px 2px`).
      // That gives the modern "lift from page" feel without resorting to
      // raw rgba values.
      className='group relative rounded-[14px] bg-tokens-neutral-bg-white border border-tokens-neutral-border-2 shadow-segment hover:shadow-button transition-shadow duration-200'
    >
      <div className='flex items-start gap-3 px-4 py-3.5'>
        <div className='flex-1 min-w-0 flex flex-col gap-1.5'>
          {/* Title row */}
          <div className='flex items-center gap-2 min-w-0'>
            <Typography
              fontFamily='geist'
              fontSize='bodyMdMedium'
              fontWeight='semibold'
              className={`truncate min-w-0 ${
                bookmark.title
                  ? 'text-tokens-neutral-text-primary'
                  : 'italic text-tokens-neutral-text-secondary'
              }`}
            >
              {bookmark.title || 'Untitled'}
            </Typography>
          </div>

          {/* Body preview — clamped to 2 lines so a long prompt doesn't
              dominate the row. line-clamp keeps the card visually balanced
              with shorter neighbors. */}
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            className='text-tokens-neutral-text-secondary line-clamp-2 break-words'
          >
            {bookmark.body}
          </Typography>

          {/* Metadata row — author chip + updated time, both subtle. */}
          <div className='flex items-center gap-3 mt-1 select-none'>
            <AuthorChip bookmark={bookmark} />
            <span className='w-px h-3 bg-tokens-neutral-border-2' aria-hidden='true' />
            <Typography
              fontFamily='geist'
              fontSize='labelSm'
              className='text-tokens-neutral-text-tertiary'
            >
              {formatTs(bookmark.updated_time)}
            </Typography>
          </div>
        </div>

        {/* Right slot: Edit/Delete on owned rows (fade in on hover);
            otherwise nothing — non-owners see only the read state. */}
        {bookmark.is_owner ? (
          <div className='shrink-0 flex items-start gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200'>
            <button
              type='button'
              onClick={onEdit}
              data-testid={`bookmarks-form-edit-${bookmark.uuid}`}
              className='inline-flex items-center justify-center h-7 px-2.5 rounded-md text-xs font-medium text-tokens-neutral-text-secondary hover:text-tokens-neutral-text-primary hover:bg-tokens-neutral-fill-fill-6 transition-colors select-none'
            >
              Edit
            </button>
            <button
              type='button'
              onClick={onDelete}
              data-testid={`bookmarks-form-delete-${bookmark.uuid}`}
              className='inline-flex items-center justify-center h-7 px-2.5 rounded-md text-xs font-medium text-tokens-status-error-text hover:bg-tokens-status-error-text/10 transition-colors select-none'
            >
              Delete
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Sibling of `SchedulableHintCard` in ScheduledTasksSettingsTab — the same
 * "what this surface does" container the rest of the settings stack
 * uses. Visually frames the explanatory copy that previously floated
 * loose under the heading and made the tab read as a smaller, less
 * substantial surface than its peers.
 */
function BookmarksHintCard() {
  return (
    <section
      className='w-full rounded-[12px] border border-tokens-neutral-border-1 bg-tokens-neutral-fill-fill-6 px-5 py-4 flex flex-col gap-3'
      data-testid='bookmarks-hint'
      aria-label='How bookmarks work'
    >
      <Typography fontFamily='geist' fontSize='bodyMdMedium' fontWeight='semibold'>
        How bookmarks work
      </Typography>
      <ul className='flex flex-col gap-1.5 list-disc pl-5'>
        <li>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            className='text-tokens-neutral-text-secondary'
          >
            <strong>Personal</strong> bookmarks stay in this workspace — only you can see them, and
            they don’t follow you when you switch workspaces.
          </Typography>
        </li>
        <li>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            className='text-tokens-neutral-text-secondary'
          >
            <strong>Shared</strong> bookmarks are visible to everyone in this workspace (Team plan
            and above). They stay scoped to the workspace they were created in — other workspaces on
            your account don’t see them.
          </Typography>
        </li>
        <li>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            className='text-tokens-neutral-text-secondary'
          >
            Only the author can edit or delete a bookmark — even when it’s shared with the
            workspace.
          </Typography>
        </li>
      </ul>
    </section>
  )
}

function AuthorChip({ bookmark }: { bookmark: PromptBookmark }) {
  const isOwner = bookmark.is_owner
  return (
    <span
      title={bookmark.author_email || undefined}
      data-testid={`bookmarks-form-author-${bookmark.uuid}`}
      className='inline-flex items-center gap-1.5 min-w-0'
    >
      <span
        className={`inline-flex items-center justify-center w-[18px] h-[18px] rounded-full text-[10px] font-semibold shadow-[inset_0_0_0_1px_rgba(15,15,15,0.06)] ${
          isOwner
            ? 'bg-tokens-status-link-base text-tokens-status-link-2'
            : 'bg-tokens-neutral-fill-fill-6 text-tokens-neutral-text-secondary'
        }`}
        aria-hidden='true'
      >
        {isOwner ? 'Y' : authorInitial(bookmark)}
      </span>
      <Typography
        fontFamily='geist'
        fontSize='labelSm'
        className='text-tokens-neutral-text-secondary truncate'
      >
        {authorLabel(bookmark)}
      </Typography>
    </span>
  )
}
